/**
 * Enhanced Error Tracking Utility
 * 
 * This module provides advanced utilities for tracking and logging errors
 * to help identify the source of empty error objects and other hard-to-debug issues.
 */

// Track error occurrences with timestamps
interface ErrorRecord {
  count: number;
  lastOccurrence: Date;
  occurrences: Array<{
    timestamp: Date;
    location?: string;
    stack?: string;
  }>;
}

interface ErrorStats {
  emptyErrors: ErrorRecord;
  apiErrors: ErrorRecord;
  reactErrors: ErrorRecord;
  otherErrors: ErrorRecord;
}

// Initialize error tracking stats
const errorStats: ErrorStats = {
  emptyErrors: { count: 0, lastOccurrence: new Date(), occurrences: [] },
  apiErrors: { count: 0, lastOccurrence: new Date(), occurrences: [] },
  reactErrors: { count: 0, lastOccurrence: new Date(), occurrences: [] },
  otherErrors: { count: 0, lastOccurrence: new Date(), occurrences: [] }
};

// Maximum number of error occurrences to store
const MAX_ERROR_HISTORY = 20;

/**
 * Get a formatted stack trace for debugging purposes
 */
export const getStackTrace = (message: string): string => {
  try {
    throw new Error(message);
  } catch (err) {
    if (err instanceof Error && err.stack) {
      // Format the stack trace to be more readable
      return err.stack
        .split('\n')
        .slice(1) // Remove the first line which is the error message
        .map(line => line.trim())
        .join('\n');
    }
    return 'No stack trace available';
  }
};

/**
 * Log a stack trace for debugging purposes
 */
export const logStackTrace = (message: string): void => {
  const stack = getStackTrace(message);
  console.debug(`Stack trace for ${message}:
${stack}`);
}

/**
 * Track an empty error occurrence with detailed information
 */
export const trackEmptyError = (location: string = 'unknown'): void => {
  const stack = getStackTrace('Empty error stack trace');
  
  errorStats.emptyErrors.count++;
  errorStats.emptyErrors.lastOccurrence = new Date();
  
  // Add to occurrences with limited history
  errorStats.emptyErrors.occurrences.unshift({
    timestamp: new Date(),
    location,
    stack
  });
  
  // Trim history if needed
  if (errorStats.emptyErrors.occurrences.length > MAX_ERROR_HISTORY) {
    errorStats.emptyErrors.occurrences = 
      errorStats.emptyErrors.occurrences.slice(0, MAX_ERROR_HISTORY);
  }
  
  console.debug(`Empty error tracked at ${location} (count: ${errorStats.emptyErrors.count})`);
  console.debug(`Stack trace:
${stack}`);
};

/**
 * Track an API error occurrence
 */
export const trackApiError = (endpoint: string, error: any): void => {
  const stack = getStackTrace(`API error at ${endpoint}`);
  
  errorStats.apiErrors.count++;
  errorStats.apiErrors.lastOccurrence = new Date();
  
  errorStats.apiErrors.occurrences.unshift({
    timestamp: new Date(),
    location: endpoint,
    stack
  });
  
  if (errorStats.apiErrors.occurrences.length > MAX_ERROR_HISTORY) {
    errorStats.apiErrors.occurrences = 
      errorStats.apiErrors.occurrences.slice(0, MAX_ERROR_HISTORY);
  }
};

/**
 * Track a React component error
 */
export const trackReactError = (componentName: string, error: any): void => {
  const stack = getStackTrace(`React error in ${componentName}`);
  
  errorStats.reactErrors.count++;
  errorStats.reactErrors.lastOccurrence = new Date();
  
  errorStats.reactErrors.occurrences.unshift({
    timestamp: new Date(),
    location: componentName,
    stack
  });
  
  if (errorStats.reactErrors.occurrences.length > MAX_ERROR_HISTORY) {
    errorStats.reactErrors.occurrences = 
      errorStats.reactErrors.occurrences.slice(0, MAX_ERROR_HISTORY);
  }
};

/**
 * Reset error tracking counters
 */
export const resetErrorTracking = (): void => {
  errorStats.emptyErrors.count = 0;
  errorStats.emptyErrors.occurrences = [];
  errorStats.apiErrors.count = 0;
  errorStats.apiErrors.occurrences = [];
  errorStats.reactErrors.count = 0;
  errorStats.reactErrors.occurrences = [];
  errorStats.otherErrors.count = 0;
  errorStats.otherErrors.occurrences = [];
  
  console.debug('Error tracking reset');
};

/**
 * Get current error statistics
 */
export const getErrorStats = (): ErrorStats => {
  return errorStats;
};

/**
 * Monitor a function for errors with detailed tracking
 * @param fn The function to monitor
 * @param name Optional name for the function
 * @param type Type of function (api, react, other)
 */
export function monitorFunction<T extends (...args: any[]) => any>(
  fn: T,
  name: string = 'unnamed',
  type: 'api' | 'react' | 'other' = 'other'
): (...args: Parameters<T>) => ReturnType<T> {
  return (...args: Parameters<T>): ReturnType<T> => {
    try {
      return fn(...args);
    } catch (error) {
      // Track the error based on type
      if (type === 'api') {
        trackApiError(name, error);
      } else if (type === 'react') {
        trackReactError(name, error);
      } else {
        // Track as other error
        errorStats.otherErrors.count++;
        errorStats.otherErrors.lastOccurrence = new Date();
      }
      
      console.error(`Error in monitored function ${name}:`, error);
      logStackTrace(`Error in ${name}`);
      throw error;
    }
  };
}

/**
 * Add error tracking to a React component
 * This function is commented out as it requires JSX transformation
 * and will be implemented when needed
 */
// export function withErrorTracking<P extends object>(
//   Component: React.ComponentType<P>,
//   componentName: string
// ): React.FC<P> {
//   return (props: P) => {
//     try {
//       // JSX transformation would happen here
//       return null; // Placeholder
//     } catch (error) {
//       trackReactError(componentName, error);
//       console.error(`Error in component ${componentName}:`, error);
//       throw error;
//     }
//   };
// }

/**
 * Track error sources with stack traces
 */
export const initErrorTracker = (): void => {
  // Store the original console.error
  const originalConsoleError = console.error;
  
  // Override console.error to add stack trace for empty errors
  console.error = function(...args) {
    // Check if this matches the [ERROR] {} pattern
    if (args.length === 2 && args[0] === '[ERROR]' && 
        (args[1] === '{}' || 
         (typeof args[1] === 'object' && Object.keys(args[1]).length === 0))) {
      // Create an error to capture the stack trace
      const stackTrace = new Error('Empty error object stack trace').stack;
      
      // Log the intercepted error with stack trace for debugging
      console.debug('[DIAGNOSTIC] Empty error object detected with stack trace:', stackTrace);
      
      // Add a breakpoint here when debugging in Chrome DevTools
      // debugger;
      
      // Return without logging the original error
      return;
    }
    
    // Otherwise, pass through to the original console.error
    originalConsoleError.apply(console, args);
  };
  
  // Monitor all promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (!event.reason || 
        (typeof event.reason === 'object' && Object.keys(event.reason).length === 0)) {
      console.debug('[DIAGNOSTIC] Unhandled empty rejection detected at:', new Error().stack);
      event.preventDefault();
    }
  });
  
  // Monitor React errors
  const originalError = console.error;
  const errorListener = (event: ErrorEvent) => {
    console.debug('[DIAGNOSTIC] Global error event detected:', event.error || event.message);
    console.debug('Error location:', event.filename, 'line:', event.lineno, 'col:', event.colno);
    console.debug('Stack trace:', event.error?.stack || 'No stack trace available');
  };
  
  window.addEventListener('error', errorListener);
  
  console.debug('[DIAGNOSTIC] Error tracker initialized');
};
